import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import App from './App';
import { RootStore } from '../stores/root';
import { createBrowserEnvironment } from '../platform/browserEnvironment';
import { Router } from 'react-router-dom';
import { WixDesignSystemProvider } from '@wix/design-system';
import { theme } from '@wix/design-system/dist/cjs/Themes/brand';
import { useExperiments } from '@wix/wix-experiments-react';
import { AccountSettingsHttpApi } from '../services/accountSettingsApi';
import { i18n as i18nType } from 'i18next';
import { Logger } from '@wix/web-bi-logger/dist/src/types';
import { DealerApi } from '../services/dealerApi';
import { createBiLogger } from '../services/biLogger';

import '@wix/design-system/styles.global.css';
import { PasswordStrengthExtractor } from '@wix/identity-fed-common';
import { BiProfileServiceApi } from '../services/biProfileApi';

export interface AppContext {
  rootStore: RootStore;
}

interface AppLoaderProps {
  createAccountSettingsApi: (biLogger: Logger) => AccountSettingsHttpApi;
  createDealerApi: () => DealerApi;
  i18n: i18nType;
}

export const AppContextProvider = React.createContext<AppContext>(undefined!);
export const AppLoader: React.FC<AppLoaderProps> = observer(
  ({ i18n, createAccountSettingsApi, createDealerApi }) => {
    const { experiments } = useExperiments();
    const [externals] = useState(() => {
      const biLogger = createBiLogger();
      const accountSettingsApi = createAccountSettingsApi(biLogger);
      const biProfileServiceApi = new BiProfileServiceApi();
      const dealerApi = createDealerApi();
      const passwordStrengthExtractor = new PasswordStrengthExtractor();
      // The Yoshi host builds the browser environment here for now; a later
      // phase hands `env` in as a prop so the same tree can be server-rendered.
      const env = createBrowserEnvironment();

      const rootStore = new RootStore({
        env,
        i18n,
        experiments,
        accountSettingsApi,
        biProfileServiceApi,
        dealerApi,
        passwordStrengthExtractor,
      });
      globalThis.rootStore = rootStore;

      return {
        rootStore,
      };
    });

    // Client-only lifecycle: reporting, listeners and SDK reads run after the
    // first render, never during construction. Guarded against StrictMode's
    // double effect invocation by RootStore.start()'s own idempotency check.
    useEffect(() => {
      void externals.rootStore.start();
    }, [externals.rootStore]);
    // Preset can opt out of Brand theme to allow full CSS variable theming
    const useBrandTheme =
      externals.rootStore.displayStore.preset.useBrandTheme !== false;

    return (
      <AppContextProvider.Provider value={{ rootStore: externals.rootStore }}>
        <WixDesignSystemProvider
          features={{ newColorsBranding: true }}
          theme={useBrandTheme ? theme() : undefined}
        >
          <Router history={externals.rootStore.navigationStore.history}>
            <App data-hook="appRootDataHook" />
          </Router>
        </WixDesignSystemProvider>
      </AppContextProvider.Provider>
    );
  },
);
